var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/event-contract.ts
import { z } from "zod";
var TelemetryEventTypeSchema = z.enum([
  "exception",
  "handled_error",
  "unhandled_rejection",
  "client_crash",
  "http_request_failure",
  "validation_failure",
  "user_action_outcome",
  "business_invariant_failure",
  "job_failure",
  "dependency_failure",
  "data_consistency_failure",
  "telemetry_delivery_failure"
]);
var TelemetrySeveritySchema = z.enum([
  "debug",
  "info",
  "warn",
  "error",
  "fatal"
]);
var TelemetryPlatformSchema = z.enum([
  "web",
  "agent-mobile",
  "customer-mobile",
  "api",
  "worker",
  "cli",
  "unknown"
]);
var TelemetryEventSchema = z.object({
  schema_version: z.string().default("1.0.0"),
  event_id: z.string().uuid(),
  event_type: TelemetryEventTypeSchema,
  timestamp: z.string().datetime({
    message: "timestamp must be UTC ISO 8601"
  }),
  environment: z.string().min(1),
  service_name: z.string().min(1),
  release_id: z.string().default("unavailable"),
  platform: TelemetryPlatformSchema,
  user_id: z.string().nullable().optional(),
  user_role: z.string().nullable().optional(),
  session_id: z.string().nullable().optional(),
  operation_id: z.string().nullable().optional(),
  request_id: z.string().nullable().optional(),
  trace_id: z.string().nullable().optional(),
  route_or_screen: z.string().nullable().optional(),
  severity: TelemetrySeveritySchema,
  attributes: z.record(z.string(), z.unknown()).default({})
});

// src/redaction.ts
var SENSITIVE_KEY_PATTERNS = [
  /password/i,
  /passwd/i,
  /secret/i,
  /token/i,
  /bearer/i,
  /authorization/i,
  /cookie/i,
  /mpin/i,
  /\bpin\b/i,
  /otp/i,
  /cvv/i,
  /pan/i,
  /aadhaar/i,
  /aadhar/i,
  /account_?number/i,
  /card_?number/i
];
var AADHAAR_REGEX = /\b\d{4}[ -]?\d{4}[ -]?\d{4}\b/g;
var PAN_REGEX = /\b[A-Z]{5}[0-9]{4}[A-Z]{1}\b/g;
var JWT_REGEX = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
var REDACTED_MASK = "[REDACTED]";
function isSensitiveKey(key) {
  return SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));
}
__name(isSensitiveKey, "isSensitiveKey");
function sanitizeString(value) {
  let sanitized = value;
  sanitized = sanitized.replace(JWT_REGEX, REDACTED_MASK);
  sanitized = sanitized.replace(AADHAAR_REGEX, REDACTED_MASK);
  sanitized = sanitized.replace(PAN_REGEX, REDACTED_MASK);
  return sanitized;
}
__name(sanitizeString, "sanitizeString");
function sanitizePayload(input, depth = 0, seen = /* @__PURE__ */ new WeakSet()) {
  if (depth > 12) {
    return REDACTED_MASK;
  }
  if (input === null || input === void 0) {
    return input;
  }
  if (typeof input === "string") {
    return sanitizeString(input);
  }
  if (typeof input === "number" || typeof input === "boolean") {
    return input;
  }
  if (typeof input === "object") {
    if (seen.has(input)) {
      return "[CIRCULAR]";
    }
    seen.add(input);
    if (Array.isArray(input)) {
      return input.map((item) => sanitizePayload(item, depth + 1, seen));
    }
    const output = {};
    for (const [key, value] of Object.entries(input)) {
      if (isSensitiveKey(key)) {
        output[key] = REDACTED_MASK;
      } else {
        output[key] = sanitizePayload(value, depth + 1, seen);
      }
    }
    return output;
  }
  return REDACTED_MASK;
}
__name(sanitizePayload, "sanitizePayload");

// src/w3c-trace.ts
var TRACEPARENT_REGEX = /^([0-9a-f]{2})-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/i;
function getRandomHex(byteLength) {
  if (typeof globalThis !== "undefined" && globalThis.crypto?.getRandomValues) {
    const bytes = new Uint8Array(byteLength);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  let hex = "";
  for (let i = 0; i < byteLength; i++) {
    hex += Math.floor(Math.random() * 256).toString(16).padStart(2, "0");
  }
  return hex;
}
__name(getRandomHex, "getRandomHex");
function generateTraceId() {
  return getRandomHex(16);
}
__name(generateTraceId, "generateTraceId");
function generateSpanId() {
  return getRandomHex(8);
}
__name(generateSpanId, "generateSpanId");
function parseTraceparent(header) {
  if (!header || typeof header !== "string") return null;
  const match = TRACEPARENT_REGEX.exec(header.trim());
  if (!match) return null;
  const [, version, traceId, spanId, flags] = match;
  if (/^0+$/.test(traceId) || /^0+$/.test(spanId)) {
    return null;
  }
  return {
    version: version.toLowerCase(),
    traceId: traceId.toLowerCase(),
    spanId: spanId.toLowerCase(),
    flags: flags.toLowerCase()
  };
}
__name(parseTraceparent, "parseTraceparent");
function createTraceparent(traceId = generateTraceId(), spanId = generateSpanId(), flags = "01") {
  return `00-${traceId}-${spanId}-${flags}`;
}
__name(createTraceparent, "createTraceparent");

export {
  __name,
  TelemetryEventTypeSchema,
  TelemetrySeveritySchema,
  TelemetryPlatformSchema,
  TelemetryEventSchema,
  REDACTED_MASK,
  isSensitiveKey,
  sanitizeString,
  sanitizePayload,
  generateTraceId,
  generateSpanId,
  parseTraceparent,
  createTraceparent
};
//# sourceMappingURL=chunk-T6EFHZLA.js.map